import { SITE_QUERY_PARAM } from 'layers/content/v2/Hocs/withSiteContext/constants'
import {
  DASHBOARD_ROUTE,
  isHighSchoolDashboard,
  isHighSchoolUrl,
} from 'utils/highschoolHelpers'
import {
  isDigitalWellBeingUrl,
  isK5Url,
  isK5Dashboard,
  isMiddleSchoolUrl,
  isMiddleSchoolDashboard,
} from 'utils/routing/routeHelpers'

const LESSONS_URL_BY_PROGRAM = {
  k5: '/product/elementary/lessons',
  middleSchool: '/product/middle-school/lessons',
  digitalWellBeing: '/product/digital-well-being/lessons',
}

const lessonsLandingWithSite = (programKey, siteId) =>
  `${LESSONS_URL_BY_PROGRAM[programKey]}?${SITE_QUERY_PARAM}=${siteId}`

const buildHsUrlAfterSiteChange = ({ currentHref, siteId, isNewSiteSetup }) => {
  if (!currentHref) {
    return `${DASHBOARD_ROUTE}?${SITE_QUERY_PARAM}=${siteId}`
  }

  const currentUrl = new URL(currentHref)
  currentUrl.searchParams.delete('class')

  if (isNewSiteSetup) {
    currentUrl.searchParams.set('mode', 'teach')
    currentUrl.searchParams.set(SITE_QUERY_PARAM, siteId)
  } else {
    currentUrl.searchParams.set('mode', 'preview')
    currentUrl.searchParams.delete(SITE_QUERY_PARAM)
  }

  return `${currentUrl.pathname}${currentUrl.search}`
}

const setSiteOnCurrentHref = (currentHref, siteId) => {
  if (!currentHref) {
    return `${DASHBOARD_ROUTE}?${SITE_QUERY_PARAM}=${siteId}`
  }

  const currentUrl = new URL(currentHref)
  currentUrl.searchParams.set(SITE_QUERY_PARAM, siteId)
  return `${currentUrl.pathname}${currentUrl.search}`
}

/** Fallback when programType is omitted — only safe in browser contexts. */
const resolveProgramFromLocation = () => {
  if (typeof window === 'undefined') return null

  if (isHighSchoolDashboard() || isMiddleSchoolDashboard() || isK5Dashboard()) {
    return 'dashboard'
  }

  // Native DWB before K5 — grade-k DWB URLs can match K5 helpers.
  if (isDigitalWellBeingUrl()) return 'digitalWellBeing'
  if (isK5Url()) return 'k5'
  if (isMiddleSchoolUrl()) return 'middleSchool'
  if (isHighSchoolUrl()) return 'highSchool'

  return 'unknown'
}

export const resolveUrlAfterSiteChange = ({
  siteId,
  programType,
  isNewSiteSetup = true,
  redirectTo,
  isOnDashboard = false,
  currentHref,
}) => {
  if (!siteId) {
    return DASHBOARD_ROUTE
  }

  if (isOnDashboard) {
    return `${redirectTo}?${SITE_QUERY_PARAM}=${siteId}`
  }

  if (programType === 'digitalWellBeing') {
    return lessonsLandingWithSite('digitalWellBeing', siteId)
  }

  if (programType === 'k5') {
    return lessonsLandingWithSite('k5', siteId)
  }

  if (programType === 'middleSchool') {
    return lessonsLandingWithSite('middleSchool', siteId)
  }

  if (programType === 'highSchool') {
    return buildHsUrlAfterSiteChange({
      currentHref,
      siteId,
      isNewSiteSetup,
    })
  }

  const locationProgram = resolveProgramFromLocation()

  if (locationProgram === 'dashboard') {
    return `${redirectTo}?${SITE_QUERY_PARAM}=${siteId}`
  }

  if (locationProgram === 'digitalWellBeing') {
    return lessonsLandingWithSite('digitalWellBeing', siteId)
  }

  if (locationProgram === 'k5') {
    return lessonsLandingWithSite('k5', siteId)
  }

  if (locationProgram === 'middleSchool') {
    return lessonsLandingWithSite('middleSchool', siteId)
  }

  if (locationProgram === 'highSchool') {
    return buildHsUrlAfterSiteChange({
      currentHref,
      siteId,
      isNewSiteSetup,
    })
  }

  return setSiteOnCurrentHref(currentHref, siteId)
}
